import { berlinUhrzeit } from "@dj/domain/gemeinsam";
import { abstandUndRichtung, entfernungText, naechstgelegen } from "@dj/domain/lage";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import { Schutzflaeche } from "../design/Hinweis";
import { Kachel } from "../design/Kachel";
import { Scrollbereich } from "../design/Scrollbereich";
import { useSperre } from "../design/sperre";
import { Symbol } from "../design/Symbol";
import { umschlag, type Umschlag } from "../plattform/api";
import { anrufen, hoechstens, navigieren, positionMessen, routeZu, type Messung } from "../plattform/position";
import { beobachten, eintraege, einreihen, STANDSYMBOL, type Uebertragungsstand } from "../plattform/warteschlange";
import { positionText, useDrueckjagdSammlung, useMeineAufgaben, type Rahmen } from "./daten";
import { rettungspunkteAus } from "./eingehend";

/**
 * Notruf (oberflaeche.md §5, design/README §9.10; F-170 … F-173, ADR-0023): höchstens zwei Fingertipps ab der
 * Startansicht. Das Notrufblatt (`main` „Notruf“) zeigt „Person verletzt“, „sonstiger Notfall“ und den Bereich
 * „Hund verletzt“ mit einer Kachel je Hund im Treiben und „Hund verletzt“ für einen fremden oder unbekannten Hund (V-9).
 * Der zweite Fingertipp setzt den Notruf sofort ab — ohne Rückgängig (H14), über die Warteschlange, damit er ohne Netz
 * nachgereicht wird (ADR-0020) — und stellt den Anruf in den Vordergrund: `region` „Anruf 112“ mit dem nächstgelegenen
 * Rettungspunkt zur Position des Geräts (aus den Daten auf dem Gerät, K48) bzw. „Anruf <Tierarzt>“ mit Telefon und Route.
 * Position und Zeit erfasst das Gerät; ohne Position wird trotzdem abgesetzt (LIM-0009). Die App ergänzt die 112, sie
 * ersetzt sie nicht.
 */
const WARTEN_AUF_POSITION_MS = 1500;

interface Abgesetzt {
  umschlag: Umschlag;
  notfallart: string;
  messung: Messung | null;
}

function useUebertragungsstand(meldungsId: string | null): Uebertragungsstand | null {
  const [stand, setStand] = useState<Uebertragungsstand | null>(null);
  useEffect(() => {
    if (!meldungsId) return undefined;
    let aktiv = true;
    const lesen = () => void eintraege().then((alle) => {
      if (aktiv) setStand(alle.find((e) => e.meldungsId === meldungsId)?.stand ?? null);
    });
    lesen();
    const abmelden = beobachten(lesen);
    return () => { aktiv = false; abmelden(); };
  }, [meldungsId]);
  return stand;
}

export function NotrufAblauf({ rahmen }: { rahmen: Rahmen }) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const aufgaben = useMeineAufgaben(id);
  const hunde = useDrueckjagdSammlung(id, "hunde_im_treiben");
  const notfallkontakte = useDrueckjagdSammlung(id, "notfallkontakte");
  const rettungspunkte = useDrueckjagdSammlung(id, "rettungspunkte");
  const messung = useRef<Promise<Messung | null> | null>(null);
  const [vorab, setVorab] = useState<Messung | null>(null);
  const [abgesetzt, setAbgesetzt] = useState<Abgesetzt | null>(null);
  const gesperrt = useSperre("kritisch", abgesetzt ? "anruf" : "blatt");
  useEffect(() => {
    messung.current = positionMessen();
    void messung.current.then(setVorab);
  }, []);

  const absetzen = async (notfallart: string, hund: string | null) => {
    if (abgesetzt) return;
    const u = umschlag({ "Drückjagd": id, Notfallart: notfallart, ...(hund ? { Hund: hund } : {}) });
    setAbgesetzt({ umschlag: u, notfallart, messung: vorab });
    const m = vorab ?? (messung.current ? await hoechstens(messung.current, WARTEN_AUF_POSITION_MS) : null);
    if (m) {
      u.daten = { ...u.daten, Position: m.position, Genauigkeit: m.genauigkeit };
      setAbgesetzt({ umschlag: u, notfallart, messung: m });
    }
    await einreihen("NotrufAbsetzen", `Notruf ${u.meldungsId}`, u);
  };

  const zurStartansicht = () => navigate(`/drueckjagd/${id}`, { replace: true });

  if (abgesetzt) {
    const tierarzt = abgesetzt.notfallart === "Hund verletzt" ? notfallkontakte.find((k) => k["Art"] === "Tierarzt") ?? null : null;
    return rahmen({
      drueckjagdId: id, name: "Notruf", art: "anruf", titel: "Notruf", titelSymbol: "notruf", sperre: gesperrt,
      schritte: tierarzt ? String(tierarzt["Bezeichnung"]) : "112",
      children: <Anruf abgesetzt={abgesetzt} aufgaben={aufgaben} notfallkontakte={notfallkontakte}
        rettungspunkte={rettungspunkte} onSchliessen={zurStartansicht} />,
    });
  }

  const eigeneHunde = [...hunde].sort((a, b) => String(a["Hund"]).localeCompare(String(b["Hund"]), "de"));
  // nächstgelegener Rettungspunkt zur eigenen Position: auf hohen Bildschirmen in der freien Fläche über „Hund verletzt“
  const punkt = vorab ? naechstgelegen(vorab.position, rettungspunkteAus(rettungspunkte)) : null;
  // Notrufblatt (design/README §4.5, §9.10; Referenz `#s-notruf`): „Hund verletzt“ oben, darunter „Person verletzt“ und
  // „sonstiger Notfall“, unten im Abdruck der Notruf-Kachel nur „Zurück“ und die eigene Position.
  return rahmen({
    drueckjagdId: id, name: "Notruf", art: "notruf", titel: "Notruf", titelSymbol: "notruf", sperre: gesperrt,
    children: (
      <>
        <Kachel symbol="zurueck" begriff="Zurück" art="kompakt" className="zurueck-notruf" onPress={zurStartansicht} />
        {punkt && vorab ? (
          <Schutzflaeche className="not__rp" zeilen={[<><b>Rettungspunkt {punkt.Kennung}</b> · {entfernungText(abstandUndRichtung(vorab.position, punkt).meter)}</>]} />
        ) : null}
        <section className="not__hund" role="region" aria-labelledby="notruf-hund">
          <h2 className="not__kopf platte" id="notruf-hund">Hund verletzt</h2>
          <div className="raster raster--3" data-gleich="">
            {eigeneHunde.map((h) => (
              <Kachel key={h._id} symbol="hund" begriff={String(h["Hund"])} art="wahl" className="k--not" onPress={() => void absetzen("Hund verletzt", h._id)} />
            ))}
            <Kachel symbol="hund-verletzt" begriff="Hund verletzt" art="wahl" className="k--not" onPress={() => void absetzen("Hund verletzt", null)} />
          </div>
        </section>
        <Kachel symbol="person-verletzt" begriff="Person verletzt" art="wahl" className="k--not not__pv" onPress={() => void absetzen("Person verletzt", null)} />
        <Kachel symbol="sonstiger-notfall" begriff="sonstiger Notfall" art="wahl" className="k--not not__sn" onPress={() => void absetzen("sonstiger Notfall", null)} />
        <div className="not__pos platte" data-zeile-fest="">
          <b className="not__titel notruf-titel"><Symbol name="notruf" /><span>Notruf</span></b>
          <span>eigene Position{vorab ? <> · <span className="kennung">± {vorab.genauigkeit} m</span></> : null}</span>
          <span className="lesen kennung">{positionText(vorab?.position)}</span>
          {vorab ? <span className="not__kurz">Position übernommen · <span className="kennung">± {vorab.genauigkeit} m</span></span> : null}
        </div>
      </>
    ),
  });
}

function Anruf({ abgesetzt, aufgaben, notfallkontakte, rettungspunkte, onSchliessen }: {
  abgesetzt: Abgesetzt;
  aufgaben: ReturnType<typeof useMeineAufgaben>;
  notfallkontakte: Array<Record<string, unknown>>;
  rettungspunkte: Array<Record<string, unknown>>;
  onSchliessen: () => void;
}) {
  const stand = useUebertragungsstand(abgesetzt.umschlag.meldungsId);
  const position = abgesetzt.messung?.position ?? null;
  const punkte = rettungspunkte.map((r) => ({ Kennung: String(r["Kennung"]), Lage: (r["Lage"] as string | null) ?? null, Breite: Number(r["Breite"]), Länge: Number(r["Länge"]) }))
    .filter((r) => Number.isFinite(r.Breite) && Number.isFinite(r.Länge));
  // Rettungspunkt: der nächstgelegene zur Position des Geräts; ohne Position der aus Meine Aufgaben (K48).
  const naechster = position ? naechstgelegen(position, punkte) : null;
  const rettungspunkt = naechster ? { Kennung: naechster.Kennung, Lage: naechster.Lage }
    : aufgaben.naechsterRettungspunkt ? { Kennung: aufgaben.naechsterRettungspunkt.Kennung, Lage: aufgaben.naechsterRettungspunkt.Lage } : null;
  const ziel = rettungspunkt ? punkte.find((p) => p.Kennung === rettungspunkt.Kennung) ?? null : null;
  const tierarzt = abgesetzt.notfallart === "Hund verletzt" ? notfallkontakte.find((k) => k["Art"] === "Tierarzt") ?? null : null;
  const jagdleiter = notfallkontakte.find((k) => k["Art"] === "Jagdleiter") ?? null;
  const zielname = tierarzt ? String(tierarzt["Bezeichnung"]) : "112";
  const telefon = tierarzt ? String(tierarzt["Anruf"] ?? "") : "112";
  const status = stand === "übertragen" ? `Notruf · übertragen ${berlinUhrzeit(new Date(abgesetzt.umschlag.geraetezeit))}`
    : stand === "abgewiesen" ? "Notruf · abgewiesen" : "Notruf · noch nicht übertragen";
  // Zeilen in der Reihenfolge der Referenz (`#s-112`, `#s-tierarzt`, design/README §14 0g): beim Tierarzt zuerst
  // „Route zu“ mit dem Ziel als sichtbarem Text (für Hilfsmittel steht es schon im Namen der region), dann „Telefon“
  const zeilen: Array<[string, ReactNode, string?]> = [
    ...(tierarzt ? [
      ["Route zu", <><span aria-hidden="true">{zielname} · </span>{String(tierarzt["Adresse"] ?? "—")}</>],
      ["Telefon", telefon, "lesen"],
    ] as Array<[string, ReactNode, string?]> : []),
    ["eigene Position", positionText(position), "lesen kennung"],
    ["Rettungspunkt", rettungspunkt?.Kennung ?? "—", "lesen kennung"],
    ["Lage", rettungspunkt?.Lage ?? "—"],
    ...(tierarzt ? [] : [
      ["Route zu", ziel ? [ziel.Kennung, position ? entfernungText(abstandUndRichtung(position, ziel).meter) : null].filter(Boolean).join(" · ") : "—"],
      ["Telefon", telefon],
    ] as Array<[string, ReactNode, string?]>),
  ];
  const knoepfe = (
    <div className="raster raster--anruf">
      <Kachel symbol="telefon" begriff={tierarzt ? "Anrufen" : "112"} art="normal" className="k--anruf" onPress={() => anrufen(telefon)} />
      <Kachel symbol="navigation" begriff="Navigation" art="kompakt"
        onPress={() => (tierarzt ? routeZu(tierarzt["Adresse"] as string | null) : navigieren(ziel))} />
    </div>
  );
  const angaben = (
    <Scrollbereich>
      <table className="anrufzeilen platte" role="table">
        <tbody>
          {zeilen.map(([feld, wert, klasse]) => (
            <tr key={feld} role="row"><th scope="row" role="rowheader">{feld}</th><td role="cell" className={klasse}>{wert}</td></tr>
          ))}
        </tbody>
      </table>
    </Scrollbereich>
  );
  return (
    <>
      <section className={tierarzt ? "anruf anruf--unten" : "anruf"} role="region" aria-label={`Anruf ${zielname}`}>
        <div className="anruf__status platte" role="status" data-zeile-fest="">
          <Symbol name={STANDSYMBOL[stand ?? "noch nicht übertragen"]} /><span>{status}</span>
        </div>
        {tierarzt ? <>{angaben}{knoepfe}</> : <>{knoepfe}{angaben}</>}
      </section>
      <div className="raster raster--2 anruf__unten">
        <Kachel symbol="schliessen" begriff="Schließen" art="kompakt" onPress={onSchliessen} />
        <Kachel symbol="telefon" begriff="Jagdleiter" art="kompakt" isDisabled={!jagdleiter}
          onPress={() => anrufen(jagdleiter?.["Anruf"] as string | undefined)} />
      </div>
    </>
  );
}

